<template>
  <el-row>
    <el-col :lg="isEesti ? 14 : 24" :md="24" class="component-container">
      <div class="box-component">
        <p class="fat-text">
          {{
            $t("stageTwoTwo.recommendedModuleAmount", {
              modules: calculationData.modules,
            })
          }}
        </p>
        <div class="solar-panels-wrapper">
          <div class="solar-panels" v-if="calculationData.modules > 0">
            <div
              v-for="i in numModules"
              :key="i"
              class="solar-panel active"
              :style="`width: ${
                calculatedModuleSize * 2
              }px; height: ${calculatedModuleSize}px`"
            ></div>
          </div>
          <div class="solar-panels" v-else>
            <div
              v-for="i in 50"
              :key="i"
              class="solar-panel inactive"
              :style="`width: 37px; height: 37px`"
            ></div>
          </div>
        </div>
        <div class="extra-modules" v-if="numModules === maxModules">
          <p class="extra-modules-text">
            {{
              `+ ${calculationData.modules - numModules} ${$t(
                "stageTwoTwo.addedModules"
              )}`
            }}
          </p>
        </div>
      </div>
    </el-col>
    <el-col
      :md="24"
      :lg="10"
      v-if="isEesti && calculatedPrice[0] > 0"
      class="component-container"
    >
      <div class="box-component">
        <p class="fat-text">
          {{ t("stageTwoTwo.price") }}
        </p>
        <h3 class="large-text text-center">
          {{ calculatedPrice[0] }} €
        </h3>
        <p class="light-text text-center">{{ t("stageTwoTwo.priceInfo") }}</p>
      </div>
    </el-col>
  </el-row>
  <el-row>
    <el-col :lg="12" :sm="24" class="component-container">
      <div class="box-component">
        <p class="fat-text">
          {{ $t("stageTwoTwo.selectPolygons") }}
        </p>
        <div class="checkbox-container">
          <el-checkbox
            :key="index"
            v-for="(polygon, index) in polygons"
            @change="polygonChecked(index, $event)"
            :checked="checked[index]"
            :label="$t('general.polygon') + ' ' + (index + 1)"
          />
          <p class="light-text">
            {{ $t("stageTwoTwo.obstructionNotice") }}
          </p>
        </div>
      </div>
    </el-col>
    <el-col :lg="12" :sm="24"
      ><el-row>
        <el-col :lg="24" :md="12" :sm="24" class="component-container">
          <div class="box-component">
            <p class="fat-text">
              {{ $t("stageTwoTwo.inverterSize") }}
            </p>
            <h3 class="large-text">{{ calculationData.inverterPower }} kW</h3>
          </div>
        </el-col>
        <el-col :lg="24" :md="12" :sm="24" class="component-container">
          <div class="box-component">
            <p class="fat-text" style="margin-bottom: 1.625rem">
              {{ $t("stageTwoTwo.powerSize") }}
            </p>
            <h3 class="large-text">{{ calculationData.modulePower }} kW</h3>
          </div>
        </el-col>
      </el-row></el-col
    >
  </el-row>
  <el-row>
    <el-col :span="24" class="component-container sliders-container">
      <div class="box-component">
        <el-row :gutter="24">
          <el-col :lg="24" :md="24">
            <p class="fat-text">{{ t("stageTwoTwo.yearlyUsage") }}</p>
            <p class="light-text text-center">
              {{ t("stageTwoTwo.kiloWattYearly") }}
            </p>
            <el-slider
              class="year-slider"
              :show-tooltip="true"
              :min="1000"
              :max="40000"
              :step="100"
              v-model="yearConsumption"
              :format-tooltip="formatYearToolTip"
              :show-input="true"
              :show-input-controls="false"
              @change="updateStats"
              v-if="!sliderBreakpoint"
            />
            <el-input-number
              size="large"
              :min="1000"
              :max="40000"
              :step="100"
              class="year-input"
              v-model="yearConsumption"
              @change="updateStats"
              v-else
            />
            <div class="boxed-text">
              <p>{{ yearConsumption }} kWh</p>
            </div>
          </el-col>
          <el-col :lg="24" :md="24">
            <p class="fat-text">{{ t("stageTwoTwo.pricePerKiloWatt") }}</p>
            <p class="light-text text-center">{{ t("stageTwoTwo.pricePerKiloWattUnit") }}</p>
            <el-slider
              class="price-slider"
              :show-tooltip="true"
              :min="0.1"
              :step="0.1"
              :max="100"
              v-model="costPerkWh"
              :marks="priceMarks"
              :format-tooltip="formatCostToolTip"
              :show-input="true"
              :show-input-controls="false"
              @change="updateStats"
              v-if="!sliderBreakpoint"
            />
            <el-input-number
              size="large"
              :min="0.1"
              :max="100"
              :step="0.1"
              class="price-input"
              v-model="costPerkWh"
              @change="updateStats"
              v-else
            />
            <div class="boxed-text">
              <p>{{ costPerYear }} €/{{ t("stageTwoTwo.year") }}</p>
            </div>
          </el-col>
        </el-row>
      </div>
    </el-col>
  </el-row>
</template>

<script lang="ts" setup>
import type { CalculationData, Marks, Polygon } from "@/types";
import { computed, reactive, ref, toRef } from "vue";
import { useI18n } from "vue-i18n";
import { get, useMediaQuery } from "@vueuse/core";

const { t, locale } = useI18n();

const isEesti = computed(() => locale.value === "et");

const props = defineProps<{
  polygons: Polygon[][];
  checked: boolean[];
  calculationData: CalculationData;
  calculatedPrice: number[];
}>();

const calcData = toRef(props, "calculationData");

const sliderBreakpoint = useMediaQuery(
  "(max-width: 582px) or (min-width: 759px and max-width: 1024px)"
);

const emit = defineEmits(["polygonChecked", "updateCurrentStats"]);

const yearConsumption = ref<number>(
  props.calculationData.currentYearlyConsumption
);
const costPerkWh = ref<number>(props.calculationData.costPerkWh);

const priceMarks = reactive<Marks>({
  15: "15s",
  25: "25s",
  50: "50s",
});

const formatYearToolTip = (value: number) => {
  return `${value} kWh/${t("stageTwoTwo.year")}`;
};

const formatCostToolTip = (value: number) => {
  return `${value} s/kW`;
};

const costPerYear = computed(() => {
  return Math.round(get(yearConsumption) * (get(costPerkWh) / 100));
});

function updateStats() {
  emit("updateCurrentStats", {
    currentYearlyConsumption: get(yearConsumption),
    currentCostPerkWh: get(costPerkWh),
  });
}

function polygonChecked(index: number, e: any) {
  // Emits to parent component
  emit("polygonChecked", index, e);
}

const maxModules = 4000;
const numModules = computed(() => {
  return Math.min(maxModules, props.calculationData.modules);
});
const calculatedModuleSize = computed(() => {
  const maxModuleSize = 45;
  const minModuleSize = 4;
  let moduleSize =
    (maxModuleSize - minModuleSize) /
    Math.min(props.calculationData.modules, maxModules);
  return Math.min(minModuleSize + moduleSize * maxModuleSize, maxModuleSize);
});
</script>
